import type { Enemy } from '../Enemy';
import type { MovementBehavior } from '../behaviors/MovementBehavior';
import type { BossConfig, PhaseModifiers } from '../BossConfig';
import {
  getFieldTopY,
  getFieldBottomY,
  getFieldLeftX,
  getFieldRightX,
} from '../../constants/field';
import { getEntityScale } from '../../constants/scale';

export type ChargingPhase = 'idle' | 'windup' | 'charge' | 'recovery';

export class ChargingBossBehavior implements MovementBehavior {
  public phase: ChargingPhase = 'idle';
  private phaseElapsedMs = 0;
  // Счётчик ЗАВЕРШЁННЫХ циклов (инкрементится в transitionToIdle). На входе в windup
  // первого цикла = 0 → 0 % N = 0 → бросок. На втором цикле = 1, при N=2 пропуск.
  private cycleCount = 0;
  // Текущая фаза hp-ratio (1/2/3). Меняется при пересечении порогов в config.phases.
  private lastHpPhase: 1 | 2 | 3 = 1;

  // База — set из chargingParams в init(). Effective значения — через геттеры
  // ниже, которые домножают базу на множитель текущей фазы.
  private idleMs = 1500;
  private windupMs = 500;
  private chargeSpeed = 400;
  private recoveryMs = 1000;

  private chargeDirX = 0;
  private chargeDirY = 0;
  private chargeTargetX = 0;
  private chargeTargetY = 0;

  // Публичный доступ к зафиксированному направлению броска — нужен Boss.ts
  // для зеркаления спрайта на переходе в windup. Геттер, чтобы behavior
  // оставался единственным источником истины (нет setter'а наружу).
  get chargeDirectionX(): number {
    return this.chargeDirX;
  }

  init(_enemy: Enemy, params?: Record<string, number>): void {
    if (params) {
      this.idleMs = params.idleMs ?? this.idleMs;
      this.windupMs = params.windupMs ?? this.windupMs;
      this.chargeSpeed = params.chargeSpeed ?? this.chargeSpeed;
      this.recoveryMs = params.recoveryMs ?? this.recoveryMs;
    }
    this.phase = 'idle';
    this.phaseElapsedMs = 0;
    this.cycleCount = 0;
    this.lastHpPhase = 1;
  }

  update(enemy: Enemy, deltaMs: number, target: { x: number; y: number }): void {
    this.phaseElapsedMs += deltaMs;

    // Detect hp-phase change, fire event (GameScene делает shake + flash).
    const currentHpPhase = this.getCurrentHpPhase(enemy);
    if (currentHpPhase !== this.lastHpPhase) {
      enemy.scene.events.emit('boss:phase-change', { newPhase: currentHpPhase });
      this.lastHpPhase = currentHpPhase;
    }

    switch (this.phase) {
      case 'idle':
        if (this.phaseElapsedMs >= this.getEffectiveIdleMs(enemy)) this.transitionToWindup(enemy, target);
        break;
      case 'windup':
        if (this.phaseElapsedMs >= this.windupMs) this.transitionToCharge();
        break;
      case 'charge':
        this.moveCharge(enemy, deltaMs);
        if (this.hasReachedChargeEnd(enemy)) this.transitionToRecovery(enemy);
        break;
      case 'recovery':
        if (this.phaseElapsedMs >= this.recoveryMs) this.transitionToIdle();
        break;
    }

    // Clamp в границы поля только для НЕ-charge фаз. В charge боссу позволено
    // частично выйти за край, чтобы достать игрока стоящего у самой стены.
    // Recovery вернёт обратно в поле.
    if (this.phase !== 'charge') {
      const halfSize = enemy.displayWidth / 2;
      const fieldLeft = getFieldLeftX(enemy.scene);
      const fieldRight = getFieldRightX(enemy.scene);
      const fieldTop = getFieldTopY(enemy.scene);
      const fieldBottom = getFieldBottomY(enemy.scene);
      enemy.x = Math.max(fieldLeft + halfSize, Math.min(fieldRight - halfSize, enemy.x));
      enemy.y = Math.max(fieldTop + halfSize, Math.min(fieldBottom - halfSize, enemy.y));
    }
  }

  private transitionToWindup(enemy: Enemy, target: { x: number; y: number }): void {
    this.chargeTargetX = target.x;
    this.chargeTargetY = target.y;
    const dx = this.chargeTargetX - enemy.x;
    const dy = this.chargeTargetY - enemy.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist > 0) {
      this.chargeDirX = dx / dist;
      this.chargeDirY = dy / dist;
    }
    this.phase = 'windup';
    this.phaseElapsedMs = 0;

    // Бросок снаряда. Два режима:
    //   horizontalOnly=false (Медведь): startX/Y = центр босса, target =
    //     позиция игрока. Снаряд летит по диагонали в точку игрока.
    //   horizontalOnly=true (Верблюд): startX/Y = "пасть" (signed offset от
    //     центра по направлению броска), target = startX + большое число
    //     в ту же сторону, targetY = startY. Снаряд летит строго горизонтально.
    const pa = (enemy.config as BossConfig).projectileAttack;
    if (pa?.enabled && pa.throwInPhase === 'windup') {
      const effectiveN = this.getEffectiveThrowN(enemy, pa.throwEveryNthCycle);
      const shouldThrow = this.cycleCount % effectiveN === 0;
      if (shouldThrow) {
        const scale = getEntityScale(enemy.scene);
        const sign = this.chargeDirX < 0 ? -1 : 1;
        const startX = enemy.x + sign * (pa.mouthOffsetX ?? 0) * scale;
        const startY = enemy.y + (pa.mouthOffsetY ?? 0) * scale;
        let projTargetX = target.x;
        let projTargetY = target.y;
        if (pa.horizontalOnly) {
          projTargetX = startX + sign * 10000;
          projTargetY = startY;
        }
        enemy.scene.events.emit('boss:throw-projectile', {
          startX,
          startY,
          targetX: projTargetX,
          targetY: projTargetY,
          speed: pa.projectileSpeed,
          size: pa.projectileSize,
          color: pa.projectileColor,
          blockDurationMs: pa.blockDurationMs,
          effectType: pa.effectType ?? 'block_shooting',
          slowFactor: pa.slowFactor ?? 1.0,
          textureKey: pa.textureKey,
          animKey: pa.animKey,
          spriteHeightPx: pa.spriteHeightPx,
          useArc: pa.useArc,
          gravity: pa.gravity,
          arcHeight: pa.arcHeight,
          rotationSpeed: pa.rotationSpeed,
          landSlimeConfig: pa.landSlimeConfig,
        });
      }
    }
  }

  private transitionToCharge(): void {
    this.phase = 'charge';
    this.phaseElapsedMs = 0;
  }

  private moveCharge(enemy: Enemy, deltaMs: number): void {
    const moveDistance = (this.getEffectiveChargeSpeed(enemy) * deltaMs) / 1000;
    enemy.x += this.chargeDirX * moveDistance;
    enemy.y += this.chargeDirY * moveDistance;
  }

  private getCurrentHpPhase(enemy: Enemy): 1 | 2 | 3 {
    const phases = (enemy.config as BossConfig).phases;
    if (!phases) return 1;
    const hpRatio = enemy.currentHp / enemy.maxHp;
    if (hpRatio <= phases.phase3HpThreshold) return 3;
    if (hpRatio <= phases.phase2HpThreshold) return 2;
    return 1;
  }

  private getPhaseModifiers(enemy: Enemy): PhaseModifiers | undefined {
    const phases = (enemy.config as BossConfig).phases;
    if (!phases) return undefined;
    const p = this.getCurrentHpPhase(enemy);
    if (p === 2) return phases.phase2;
    if (p === 3) return phases.phase3;
    return undefined;
  }

  private getEffectiveIdleMs(enemy: Enemy): number {
    const mul = this.getPhaseModifiers(enemy)?.idleMsMultiplier;
    return mul !== undefined ? this.idleMs * mul : this.idleMs;
  }

  private getEffectiveChargeSpeed(enemy: Enemy): number {
    const mul = this.getPhaseModifiers(enemy)?.chargeSpeedMultiplier;
    return mul !== undefined ? this.chargeSpeed * mul : this.chargeSpeed;
  }

  private getEffectiveThrowN(enemy: Enemy, baseN: number): number {
    const override = this.getPhaseModifiers(enemy)?.throwEveryNthCycle;
    return override ?? baseN;
  }

  // Charge заканчивается когда босс достиг зафиксированной точки игрока
  // (chargeTargetX/Y, проверка через знак dot product вектора-до-цели и направления),
  // либо когда улетел далеко за пределы экрана (страховка от бесконечного полёта,
  // если целевая точка была вне досягаемости).
  private hasReachedChargeEnd(enemy: Enemy): boolean {
    const toTargetX = this.chargeTargetX - enemy.x;
    const toTargetY = this.chargeTargetY - enemy.y;
    const dot = toTargetX * this.chargeDirX + toTargetY * this.chargeDirY;
    if (dot <= 0) return true;

    const margin = 150;
    if (
      enemy.x < getFieldLeftX(enemy.scene) - margin ||
      enemy.x > getFieldRightX(enemy.scene) + margin ||
      enemy.y < getFieldTopY(enemy.scene) - margin ||
      enemy.y > getFieldBottomY(enemy.scene) + margin
    ) {
      return true;
    }

    return false;
  }

  private transitionToRecovery(enemy: Enemy): void {
    this.phase = 'recovery';
    this.phaseElapsedMs = 0;
    enemy.scene.events.emit('boss:wall-impact');

    // Слизь в точке конца рывка (Медведь).
    const sa = (enemy.config as BossConfig).slimeAttack;
    if (sa?.enabled) {
      enemy.scene.events.emit('boss:leave-slime', {
        x: enemy.x,
        y: enemy.y,
        radius: sa.radius,
        color: sa.color,
        slowFactor: sa.slowFactor,
        textureKey: sa.textureKey,
        displayDiameterPx: sa.displayDiameterPx,
        lifetimeMs: sa.lifetimeMs,
      });
    }
  }

  private transitionToIdle(): void {
    this.phase = 'idle';
    this.phaseElapsedMs = 0;
    this.cycleCount += 1;
  }
}
